import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Trash2, Save } from "lucide-react";
import { useState } from "react";
import {
  adminDeleteApiKey,
  adminListApiKeys,
  adminUpsertApiKey,
} from "@/lib/admin.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/api-keys")({
  component: ApiKeysPage,
});

function ApiKeysPage() {
  const qc = useQueryClient();
  const list = useServerFn(adminListApiKeys);
  const upsert = useServerFn(adminUpsertApiKey);
  const del = useServerFn(adminDeleteApiKey);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "keys"],
    queryFn: () => list(),
  });

  const [name, setName] = useState("TWELVE_DATA_API_KEY");
  const [value, setValue] = useState("");
  const [shown, setShown] = useState<Record<string, boolean>>({});

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "keys"] });

  const saveMut = useMutation({
    mutationFn: (v: { name: string; value: string }) => upsert({ data: v }),
    onSuccess: () => {
      toast.success("API key saved");
      setValue("");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const delMut = useMutation({
    mutationFn: (n: string) => del({ data: { name: n } }),
    onSuccess: () => {
      toast.success("Deleted");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card className="border-border/60 bg-card/60 p-5">
        <h2 className="text-sm font-semibold">Add / update API key</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Shared across all users. Use UPPER_SNAKE names (e.g. <code>TWELVE_DATA_API_KEY</code>).
        </p>
        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim() || !value.trim()) return;
            saveMut.mutate({ name: name.trim(), value: value.trim() });
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="kname">Name</Label>
            <Input
              id="kname"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="TWELVE_DATA_API_KEY"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="kval">Value</Label>
            <Input
              id="kval"
              type="password"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Paste key"
            />
          </div>
          <Button type="submit" disabled={saveMut.isPending} className="w-full">
            <Save className="mr-2 h-4 w-4" />
            {saveMut.isPending ? "Saving…" : "Save key"}
          </Button>
        </form>
      </Card>

      <Card className="border-border/60 bg-card/60">
        <div className="border-b border-border/60 p-4">
          <h2 className="text-sm font-semibold">Stored keys</h2>
          <p className="text-xs text-muted-foreground">{data?.keys.length ?? 0} keys</p>
        </div>
        <div className="divide-y divide-border/40">
          {isLoading && <div className="p-4 text-sm text-muted-foreground">Loading…</div>}
          {data?.keys.map((k) => (
            <div key={k.name} className="flex items-center justify-between gap-3 p-4">
              <div className="min-w-0 flex-1">
                <div className="font-mono text-sm">{k.name}</div>
                <div className="mt-1 truncate font-mono text-xs text-muted-foreground">
                  {shown[k.name] ? k.value : "•".repeat(Math.min(24, k.value.length))}
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                  Updated {new Date(k.updated_at).toLocaleString()}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShown((s) => ({ ...s, [k.name]: !s[k.name] }))}
                >
                  {shown[k.name] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-500"
                  onClick={() => delMut.mutate(k.name)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
          {data && data.keys.length === 0 && !isLoading && (
            <div className="p-6 text-center text-sm text-muted-foreground">No keys stored.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
